import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Sparkles,
  Wrench,
  Bug,
  Smartphone,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Zap,
  Shield,
  TrendingUp,
  Calendar
} from 'lucide-react';

/**
 * Pagina Changelog per Associati UNASCA
 * Mostra solo le modifiche che impattano l'esperienza degli associati
 */
const ChangelogPage: React.FC = () => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['2026-10', '2026-09', '2026-08', '2026-07', '2026-06', '2026-05', '2026-04', '2026-03', '2026-02', '2026-01']));

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  // Badge per tipo modifica
  const Badge: React.FC<{ type: 'new' | 'improvement' | 'fix' | 'ux' | 'wip'; children: React.ReactNode }> = ({ type, children }) => {
    const styles = {
      new: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700',
      improvement: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700',
      fix: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700',
      ux: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700',
      wip: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700'
    };

    const icons = {
      new: <Sparkles className="w-3 h-3" />,
      improvement: <Wrench className="w-3 h-3" />,
      fix: <Bug className="w-3 h-3" />,
      ux: <Smartphone className="w-3 h-3" />,
      wip: <Zap className="w-3 h-3" />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${styles[type]}`}>
        {icons[type]}
        {children}
      </span>
    );
  };

  return (
    <>
      <Helmet>
        <title>Novità Piattaforma - UNASCA</title>
        <meta name="description" content="Scopri tutte le nuove funzionalità e miglioramenti per gli associati UNASCA. Sistema sondaggi, codici STA, community e molto altro." />
        <meta name="robots" content="noindex, follow" />

        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Novità Piattaforma - UNASCA" />
        <meta property="og:description" content="Scopri tutte le nuove funzionalità e miglioramenti per gli associati UNASCA" />
        <meta property="og:image" content="https://studi.unasca.it/images/changelog/changelog.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://studi.unasca.it/changelog" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Novità Piattaforma - UNASCA" />
        <meta name="twitter:description" content="Scopri tutte le nuove funzionalità e miglioramenti per gli associati UNASCA" />
        <meta name="twitter:image" content="https://studi.unasca.it/images/changelog/changelog.jpg" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Indietro
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Novità piattaforma
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Tutte le nuove funzionalità e miglioramenti implementati ed in fase di realizzazione
              </p>
            </div>
          </div>

          {/* Legenda */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Legenda</h3>
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Badge type="new">Nuovo</Badge>
                <span className="text-gray-600 dark:text-gray-400">Nuova funzionalità</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge type="improvement">Miglioramento</Badge>
                <span className="text-gray-600 dark:text-gray-400">Funzionalità migliorata</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge type="fix">Fix</Badge>
                <span className="text-gray-600 dark:text-gray-400">Correzione bug</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge type="ux">UX</Badge>
                <span className="text-gray-600 dark:text-gray-400">Miglioramento interfaccia</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge type="wip">In Lavorazione</Badge>
                <span className="text-gray-600 dark:text-gray-400">Funzionalità in sviluppo</span>
              </div>
            </div>
          </div>
        </div>

        {/* Changelog Sections */}
        <div className="space-y-4">
          {/* 2026-10 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2026-10')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Ottobre 2026</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Aggiornato: 24 Ottobre 2026</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">7 aggiornamenti</p>
                </div>
              </div>
              {expandedSections.has('2026-10') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2026-10') && (
              <div className="px-6 pb-6 space-y-6">
                {/* Eventi: verbale in una pagina leggibile */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Eventi: il verbale si apre in una pagina leggibile con il PDF ufficiale da scaricare
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">24 Ottobre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    In «I miei eventi», quando il verbale di un evento a cui il tuo studio è stato invitato viene pubblicato,
                    il pulsante «Verbale» apre una pagina dedicata con il testo, gli allegati e il pulsante «Scarica PDF»
                    per il verbale ufficiale. La pagina è disponibile anche ai dipendenti dello studio abilitati agli eventi.
                    Nell'Area Associativa anche i rappresentanti che non hanno scritto il verbale lo leggono così, una volta pubblicato.
                  </p>
                </div>

                {/* Verbali: foto e post social (Area Associativa) */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Verbali: aggiungi le foto dell'evento e proponi un post social alla Segreteria Nazionale
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">24 Ottobre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Verbali: puoi aggiungere fino a 5 foto dell'evento e proporre un post social alla Segreteria Nazionale,
                    che lo approva prima della pubblicazione. Nell'Area Associativa, se la funzione è attiva per la tua
                    carica, l'assistente prepara il testo del post senza i nomi dei presenti: tu lo rivedi, confermi che
                    le persone riconoscibili nelle foto sono d'accordo e lo invii. Le foto non entrano nel verbale e
                    vengono eliminate dalla piattaforma 30 giorni dopo l'invio.
                  </p>
                </div>

                {/* Verbali: audio e trascrizione (Area Associativa) */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Verbali: registra la riunione o carica un file audio, l'AI trascrive e scrive il verbale
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">24 Ottobre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Nell'Area Associativa chi ha creato un evento, se la sua carica è abilitata, può registrare la riunione
                    dal computer oppure caricare un file audio che ha già. L'AI trascrive la registrazione riconoscendo le diverse voci: tu indichi chi
                    sta parlando, se vuoi, e l'AI scrive il verbale dalla trascrizione. Facendo clic su un paragrafo del
                    verbale puoi riascoltare il punto della riunione da cui è nato. La registrazione non si perde se la
                    connessione cade o chiudi la pagina per sbaglio: riaprendola puoi riprendere. L'audio resta sulla
                    piattaforma per 5 giorni e ricevi un avviso prima che venga eliminato; la trascrizione e il verbale
                    restano.
                  </p>
                </div>

                {/* Verbali degli eventi (Area Associativa) */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Verbali: nuova pagina a tutta larghezza, assistente che impagina il testo, versioni e PDF ufficiale
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">24 Ottobre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Nell'Area Associativa chi ha creato un evento scrive il verbale in una pagina tutta sua: il foglio a
                    sinistra e l'assistente a destra. Incolli appunti o una bozza e l'assistente li impagina nelle sezioni
                    del verbale, senza interrompersi a metà anche con testi lunghi. Puoi chiedergli di migliorare una
                    sezione o selezionare una frase per riscriverla, accorciarla o correggerla. Ogni passaggio importante
                    lascia una versione che puoi ripristinare. Prima di pubblicare puoi vedere l'anteprima del PDF
                    ufficiale. Quando pubblichi gli invitati vengono avvisati, e gli altri rappresentanti scaricano il PDF
                    dalla pagina del verbale o da quella dell'evento.
                  </p>
                </div>

                {/* Dipendenti dello studio nell'app */}
                <div className="border-l-4 border-teal-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <Badge type="wip">In arrivo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        I dipendenti dello studio anche nell'app
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">23 Ottobre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    I dipendenti dello studio entrano anche nell'app con il loro accesso: vedono solo le sezioni abilitate
                    dal titolare, hanno la loro chat e ricevono le loro notifiche. Sul telefono ognuno vede come lette solo
                    le notifiche che ha aperto lui.
                  </p>
                </div>

                {/* Chat per i dipendenti dello studio */}
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <Badge type="wip">In arrivo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Chat per i dipendenti dello studio
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">22 Ottobre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    <strong>In arrivo:</strong> UNASCA la attiverà per gradi, insieme agli accessi dei dipendenti. Ogni
                    dipendente abilitato ha la sua chat personale, con il nome dello studio e sotto il proprio.
                  </p>
                </div>

                {/* Dipendenti dello studio */}
                <div className="border-l-4 border-amber-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <Badge type="wip">In arrivo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Accessi personali per i dipendenti dello studio
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">22 Ottobre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    <strong>In arrivo:</strong> UNASCA la attiverà per gradi, prima con alcuni studi. Ogni dipendente dello
                    studio potrà avere un <strong>accesso suo</strong>, senza usare più la password del titolare. Nella scheda
                    <strong> «I miei dipendenti»</strong> crei gli accessi per i tuoi dipendenti con nome,
                    cognome e una password che scegli tu (non serve la loro email; deve essere diversa da quella dello studio).
                    Per ognuno decidi <strong>quali sezioni può usare</strong>: Verifica Takata, Circolari, Svincolo atti,
                    Rimborso voucher, Supporto, Eventi e dirette, Sondaggi e le altre. Il dipendente entra da «Accesso associati»
                    con la scheda <strong>«Sono un dipendente»</strong>: Partita IVA dello studio, nome e cognome, password.
                    Vede solo le sezioni che gli hai abilitato, e dove lavora resta scritto <strong>chi ha fatto cosa</strong>
                    (per esempio il nome dell'operatore sul certificato Takata). Puoi sospendere un dipendente, cambiargli le
                    sezioni, reimpostargli la password o eliminarlo in qualsiasi momento: l'effetto è immediato. Pagamenti, dati
                    dello studio e Area Associativa restano solo tuoi. Per i sondaggi scegli tu se rispondi solo tu o anche i
                    tuoi dipendenti; la risposta resta una per studio. <strong>Consiglio:</strong> dopo aver creato gli accessi,
                    cambia la password dello studio. L'accesso dall'app arriverà più avanti.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* 2026-09 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2026-09')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Settembre 2026</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Aggiornato: 30 Settembre 2026</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">6 aggiornamenti</p>
                </div>
              </div>
              {expandedSections.has('2026-09') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2026-09') && (
              <div className="px-6 pb-6 space-y-6">
                {/* Takata settembre */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="fix">Correzione</Badge>
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Verifica airbag Takata: Nissan, Mitsubishi, Subaru e camion Daimler di nuovo verificabili, esiti più precisi
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">28 Settembre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Il portale UNRAE è cambiato: con la <strong>versione 1.2.5 dell'estensione</strong> le verifiche
                    di <strong>Nissan, Mitsubishi, Subaru e camion Daimler</strong> tornano a funzionare in modalità
                    guidata, e Daihatsu si verifica sul suo portale. Riconosciamo meglio gli esiti Skoda, e i veicoli
                    con richiamo <strong>già riparato</strong> non risultano più «coinvolti». Il download del certificato
                    è più affidabile nei momenti di traffico. Aggiorna l'estensione all'ultima versione.
                  </p>
                </div>

                {/* Supporto: guide, email, rappresentanti */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <Badge type="ux">UX</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Area Associativa: supporto più completo per i rappresentanti, foto profilo e meteo della tua città
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">30 Settembre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Per i rappresentanti che gestiscono le richieste: <strong>guide</strong> con le schermate, coda a colonne
                    con la scheda «Risolti e chiusi», note interne da leggere evidenziate, scelta di <strong>come ricevere le
                    email</strong> (subito, a orari, con un riepilogo giornaliero o settimanale, oppure mai) e un
                    <strong> assistente</strong> che propone una risposta o riassume la richiesta. Puoi mettere la tua
                    <strong> foto</strong> dal popup «Modifica profilo», e la pagina iniziale ti saluta con orologio e meteo
                    della tua città.
                  </p>
                </div>

                {/* Password */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Accesso con password, accanto al codice via email
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">25 Settembre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    La pagina di accesso all'area riservata è stata rinnovata: scegli tra <strong>«Codice via email»</strong>
                    e <strong>«Password»</strong>. La password si imposta, si cambia o si rimuove dalla card
                    <strong> «Password di accesso»</strong> nel tuo profilo; se la dimentichi, la segreteria può darti una
                    password temporanea. Il codice via email resta sempre disponibile. Dopo troppi tentativi errati
                    l'accesso con password si blocca per qualche minuto, per proteggere il tuo account.
                  </p>
                </div>

                {/* Email immagini + voucher */}
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="fix">Correzione</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Immagini visibili nelle email e conferma dei voucher più chiara
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">25 Settembre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Le immagini delle nostre email ora si vedono anche su <strong>Gmail e Outlook.com</strong>. Nel
                    <strong> Rimborso Voucher</strong> il pulsante di conferma dello scambio compare solo quando servono
                    davvero: a chi prenota dopo aver ricevuto il PDF del voucher, al proprietario dopo la ricevuta del
                    pagamento. Il proprietario riceve un'email appena la ricevuta viene caricata.
                  </p>
                </div>

                {/* Supporto area riservata */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <Badge type="ux">UX</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Supporto: un unico posto per le tue richieste, anche via email
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">24 Settembre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Nell'area riservata la card <strong>«Supporto»</strong> sostituisce il Feedback: trovi l'elenco delle tue
                    richieste con i messaggi non letti e la scorciatoia <strong>«Nuova richiesta»</strong>. Le segnalazioni
                    inviate in passato sono state trasformate in richieste. Ogni richiesta ha una pagina rinnovata, come una
                    pratica: conversazione a bolle, stato sempre visibile, allegati.
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mt-2">
                    Puoi <strong>rispondere direttamente alle nostre email</strong>: la risposta arriva nella richiesta giusta.
                    Anche il modulo contatti del sito apre una richiesta con il suo numero e un link per seguirla. Quando una
                    richiesta è risolta puoi <strong>riaprirla entro 14 giorni</strong>; poi si chiude da sola, senza
                    inviarti altre email.
                  </p>
                </div>

                {/* Sessione non valida */}
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="fix">Correzione</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Area riservata ed estensione Takata: niente più «collegato» a vuoto
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">4 Settembre 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Dopo un aggiornamento di sicurezza di fine agosto, alcuni di voi risultavano collegati ma nulla
                    funzionava. Ora, se la sessione non è più valida, l'area riservata ti riporta all'accesso, e
                    l'estensione Takata (versione 1.2.4) mostra «Collegato» solo quando il collegamento è davvero attivo.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* 2026-08 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2026-08')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Agosto 2026</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Aggiornato: 31 Agosto 2026</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">1 aggiornamento</p>
                </div>
              </div>
              {expandedSections.has('2026-08') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2026-08') && (
              <div className="px-6 pb-6 space-y-6">
                {/* Takata agosto */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <Badge type="fix">Correzione</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Verifica airbag Takata: Skoda automatica, camion DAF e Mercedes, FAQ della Segreteria Nazionale
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">31 Agosto 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    La verifica <strong>Skoda</strong> è ora completamente automatica. Si possono verificare i
                    <strong> camion DAF e Mercedes</strong>, e viene riconosciuta la Toyota Urban Cruiser. Corretti gli esiti
                    di Audi (richiamo già riparato), Opel e BMW. Nel registro la sezione «Casi particolari» diventa
                    <strong> «FAQ»</strong> e raccoglie le 15 risposte della Segreteria Nazionale.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* 2026-07 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2026-07')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Luglio 2026</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Aggiornato: 31 Luglio 2026</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">8 aggiornamenti</p>
                </div>
              </div>
              {expandedSections.has('2026-07') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2026-07') && (
              <div className="px-6 pb-6 space-y-6">
                {/* Takata 29-31 luglio: registro come ingresso, pannello nuovo */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <Badge type="ux">UX</Badge>
                    <Badge type="fix">Correzione</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Verifica airbag Takata: si parte dal registro, pannello dell'estensione rinnovato e nuovi esiti
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">31 Luglio 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    La verifica ha la sua <strong>card nella dashboard</strong> (in beta) e parte dal <strong>registro</strong>,
                    con le istruzioni per l'estensione, le schermate vere e un video guida. Il pannello dell'estensione è
                    stato ridisegnato: scegli la marca da una tendina, indichi numero di pratica e
                    <strong> corrispettivo</strong> prima di iniziare, ritrovi la verifica in corso se lo riapri e puoi
                    passare al tema scuro.
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mt-2">
                    Quando il portale della casa non ha dati sul veicolo, il certificato lo dice chiaramente:
                    <strong> «Impossibile verificare presso la fonte ufficiale»</strong>. Corretti gli esiti di Ford, Tesla,
                    Honda, Volkswagen, Seat e BMW; riconosciute le MINI. Nel registro trovi le risposte ai casi particolari,
                    e le segnalazioni di malfunzionamento si fanno anche da lì indicando il telaio.
                  </p>
                </div>

                {/* Takata: certificato più completo, numero pratica, altri portali sistemati */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <Badge type="improvement">Miglioramento</Badge>
                    <Badge type="fix">Correzione</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Verifica airbag Takata: certificato più completo, numero di pratica sui documenti e altri portali sistemati
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">28 Luglio 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Il <strong>certificato porta ora il logo del tuo studio</strong>, se hai caricato la tua
                    immagine dall'area riservata. Puoi indicare il <strong>numero di pratica STA</strong> prima
                    di avviare la verifica: compare sul certificato e viene messo davanti al nome del file che
                    scarichi (per esempio <em>000375T26 Certificato-Takata-W0LJ….pdf</em>), così finisce da sé
                    nella cartella giusta. Se te ne dimentichi, puoi aggiungerlo dopo dal registro: ogni riga
                    ha ora un pulsante per completare i dati e riscaricare i documenti.
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mt-2">
                    Nella <strong>dichiarazione di presa visione</strong> puoi indicare che l'acquirente è una
                    <strong> società</strong>: in quel caso firma il legale rappresentante, e il documento
                    riporta denominazione, sede e partita IVA dell'impresa. Dal certificato è sparita la nota
                    sulla «verifica a fonte singola»: il portale della casa costruttrice è la fonte ufficiale.
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mt-2">
                    Grazie ai telai che ci avete segnalato abbiamo sistemato altri portali:{' '}
                    <strong>Mercedes-Benz, Lexus, Seat, Peugeot, Citroën, DS e Suzuki</strong> ora vengono
                    compilati e letti correttamente, e riconosciamo alcuni numeri di telaio che prima davano
                    «marca non disponibile» (diverse Suzuki e Jeep). Abbiamo inoltre corretto un caso in cui la
                    prova allegata al certificato poteva inquadrare la parte sbagliata della pagina.
                  </p>
                </div>

                {/* Takata: più portali riconosciuti, istruzioni per sito e pagina Stato dei portali */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Verifica airbag Takata: più portali riconosciuti, istruzioni su misura e nuova pagina «Stato dei portali»
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">26 Luglio 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    L'estensione ora <strong>riconosce molti più portali dei costruttori</strong>: oltre a
                    Volkswagen, Opel e Toyota si aggiungono BMW, Citroën, Peugeot, DS, Lexus, Ferrari,
                    Jaguar, Land Rover, Seat, Mazda, Suzuki e Tesla. Su ognuno sa dove inserire il numero
                    di telaio e come leggere l'esito. Il <strong>banner verde in cima al portale</strong> ti
                    dice ora cosa fare su <em>quel</em> sito (per esempio su Opel il codice di sicurezza lo
                    legge l'estensione, su Toyota servono tre passaggi) invece di un messaggio uguale per
                    tutti; e se il telaio non riesce a compilarlo, <strong>te lo dice con un avviso</strong>
                    invece di restare in silenzio. Nell'area riservata trovi inoltre la nuova pagina{' '}
                    <strong>«Stato dei portali»</strong>: per ogni marchio vedi quanta automazione offre, se
                    è stato provato su un'auto vera, quando è stato controllato l'ultima volta e quante
                    verifiche sono state fatte di recente — e da lì puoi <strong>segnalare un
                    malfunzionamento</strong>.
                  </p>
                </div>

                {/* Takata: correzione esito errato */}
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="fix">Correzione</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Verifica airbag Takata: corretto un caso in cui l'esito poteva essere registrato sbagliato
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">26 Luglio 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    In alcune situazioni la verifica poteva <strong>registrare un esito sbagliato</strong>:
                    veniva letto il testo informativo della pagina del costruttore invece del risultato vero,
                    e un veicolo non coinvolto poteva risultare coinvolto. È un difetto che poteva portare a
                    un <strong>certificato errato</strong>, quindi lo diciamo con chiarezza. Da oggi viene
                    letto <strong>solo il riquadro del risultato</strong> e, se il controllo automatico non
                    concorda con quanto letto, <strong>la verifica non viene registrata</strong>: ti viene
                    chiesto di controllare tu l'esito sul portale prima di procedere.
                  </p>
                </div>

                {/* Verifica richiamo airbag Takata */}
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Nuovo servizio: Verifica richiamo airbag Takata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">24 Luglio 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Dall'area riservata puoi ora <strong>verificare se un veicolo è coinvolto nel
                    richiamo airbag Takata</strong> partendo dal solo numero di telaio: il sistema
                    riconosce la marca, interroga la fonte ufficiale (in automatico dove possibile,
                    altrimenti ti guida al portale giusto) e <strong>registra ogni verifica</strong>.
                    Se il veicolo risulta coinvolto, genera la <strong>dichiarazione di presa visione
                    precompilata</strong> per l'acquirente; in ogni caso produce il <strong>certificato
                    di verifica</strong> in PDF, con protocollo, data e fonte consultata. Il servizio è
                    disponibile per tutti gli associati in regola con la quota. Trovi tutto lo storico
                    nel <strong>registro delle verifiche</strong>.
                  </p>
                </div>

                {/* Tesseramento: notifiche, badge e richieste integrative */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <Badge type="fix">Correzione</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Tesseramento: notifiche in campanella, badge delle tue quote e richieste integrative più semplici
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">16 Luglio 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Ora ricevi una <strong>notifica nella campanella</strong> quando la segreteria ti
                    aggiunge una quota da pagare (integrativa o sede) e per gli aggiornamenti sui tuoi
                    pagamenti. Dopo il pagamento di una quota integrativa, il <strong>settore compare
                    subito come badge</strong> nella tua card tesseramento, insieme al numero aggiornato
                    delle sedi pagate. E se richiedi una nuova quota integrativa dall'area riservata,
                    la segreteria può <strong>approvarla o rifiutarla</strong> direttamente: in entrambi
                    i casi ricevi subito la risposta via email e campanella, con il motivo in caso di rifiuto.
                  </p>
                </div>

                {/* Tesseramento: pagamenti più sicuri e quote sedi in corso d'anno */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Tesseramento: pagamenti più affidabili e quota per le nuove sedi
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">16 Luglio 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Abbiamo rivisto a fondo tutto il percorso di pagamento della quota associativa
                    (carta, bonifico e RID) per renderlo più sicuro e affidabile: niente più pagamenti
                    doppi per errore, ricevute sempre corrette e <strong>lo stato delle rate RID
                    finalmente visibile</strong> nella tua area riservata. Inoltre, se apri una{' '}
                    <strong>nuova sede secondaria durante l'anno</strong>, la segreteria può ora inviarti
                    direttamente la richiesta della quota: la paghi dall'area riservata con carta o
                    bonifico, ricevi la ricevuta dedicata e la sede <strong>compare subito sulla mappa
                    della Rete Certificata</strong> una volta confermato il pagamento.
                  </p>
                </div>

                {/* Svincolo Atti: aggiungere documenti dimenticati */}
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="fix">Correzione</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Svincolo Atti: puoi aggiungere documenti dimenticati
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">3 Luglio 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Se dopo aver inviato i documenti di una pratica di svincolo ti accorgi di averne
                    dimenticato uno, ora puoi <strong>aggiungerne altri</strong> senza dover ricominciare:
                    nel dettaglio della pratica trovi il pulsante <strong>«Aggiungi altri documenti»</strong>,
                    disponibile finché la pratica non è stata completata. Lo studio viene avvisato dei nuovi
                    documenti, ma senza ricevere email ripetute se ne carichi più di uno di seguito.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* 2026-06 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2026-06')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Giugno 2026</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Aggiornato: 26 Giugno 2026</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">10 aggiornamenti</p>
                </div>
              </div>
              {expandedSections.has('2026-06') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2026-06') && (
              <div className="px-6 pb-6 space-y-6">
                {/* Area riservata riorganizzata in due schede */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Area riservata più ordinata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">26 Giugno 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    La tua area riservata ora è organizzata in due schede: <strong>«Servizi e Azioni»</strong>,
                    che si apre per prima con tutto ciò che puoi fare (voucher, svincolo atti, collaborazioni,
                    Facile.it, eventi e altro), e <strong>«Il mio profilo»</strong> con i tuoi dati, la mappa
                    della sede, il tesseramento, la Rete Certificata e i codici STA. I servizi ancora «In arrivo»
                    sono raccolti in fondo. Se hai una quota da pagare, te lo segnaliamo subito in alto.
                  </p>
                </div>

                {/* Verbale eventi visibile agli associati */}
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Verbali degli eventi nella tua area
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">21 Giugno 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Quando viene pubblicato il verbale di un evento a cui sei stato invitato,
                    ora lo trovi direttamente in <strong>«I Miei Eventi»</strong>: sulla card dell'evento
                    compare il pulsante <strong>«Verbale»</strong> che apre il testo e gli eventuali allegati da scaricare.
                    Ti avvisiamo della pubblicazione via email e con una notifica.
                  </p>
                </div>

                {/* Supporto — Fase 2 */}
                <div className="border-l-4 border-sky-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Supporto più completo
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">18 Giugno 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-sky-500 mt-1">{'•'}</span>
                      <span>Ora puoi <strong>allegare documenti e immagini</strong> (PDF/foto) alle tue richieste di supporto e alle risposte.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-sky-500 mt-1">{'•'}</span>
                      <span>Le risposte arrivano <strong>in tempo reale</strong> mentre hai aperta la conversazione, e ricevi una <strong>notifica push</strong> sul browser quando il supporto ti risponde o risolve la richiesta.</span>
                    </li>
                  </ul>
                </div>

                {/* Nuova sezione Supporto */}
                <div className="border-l-4 border-sky-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Nuova sezione Supporto
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">18 Giugno 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-sky-500 mt-1">{'•'}</span>
                      <span>Dalla tua area riservata puoi aprire <strong>richieste di assistenza</strong> ai gruppi tematici UNASCA (es. nazionalizzazione veicoli), scegliendo il canale giusto.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-sky-500 mt-1">{'•'}</span>
                      <span>Segui ogni richiesta in un'unica conversazione, ricevi una notifica quando ti rispondono e, a problema risolto, puoi lasciare una <strong>valutazione</strong>.</span>
                    </li>
                  </ul>
                </div>

                {/* Accesso diretto a Pratiche Auto Online */}
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Accesso Pratiche Auto Online
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">18 Giugno 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'•'}</span>
                      <span>Dalla card <strong>"Partnership Facile.it"</strong> puoi accedere direttamente alla tua dashboard di <strong>Pratiche Auto Online</strong> senza reinserire le credenziali. Se hai più sedi, puoi scegliere da quale entrare.</span>
                    </li>
                  </ul>
                </div>

                {/* Sedi secondarie sulla mappa Rete Certificata */}
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Sedi secondarie più precise sulla mappa
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">17 Giugno 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'•'}</span>
                      <span>Migliorata la gestione delle <strong>sedi secondarie</strong> sulla mappa della Rete Certificata: ora vengono mostrate in modo più accurato in base alla <strong>quota annuale</strong>.</span>
                    </li>
                  </ul>
                </div>

                {/* Eventi: nuovo popup dettaglio */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Eventi: dettaglio più chiaro ed elegante
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">15 Giugno 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Quando apri il <strong>dettaglio di un evento</strong> da "I Miei Eventi", trovi una scheda rinnovata: <strong>data in evidenza</strong>, orario e luogo più leggibili, link alla mappa e il tuo <strong>QR code</strong> per il check-in ben visibile con i pulsanti per salvarlo in PNG o PDF.</span>
                    </li>
                  </ul>
                </div>

                {/* Eventi: pubblichi come carica */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Eventi: scegli con quale carica pubblichi
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">15 Giugno 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Se ricopri <strong>più di una carica</strong>, quando crei un evento ora scegli <strong>"Pubblichi come"</strong>: questo determina chi vedrà l'evento nel calendario, in base alle regole impostate per ciascuna carica.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Chi può invitare e chi vede cosa è ora <strong>configurabile</strong>: nulla cambia nel funzionamento attuale, ma diventa gestibile senza interventi tecnici.</span>
                    </li>
                  </ul>
                </div>

                {/* Comunicazioni tra rappresentanti */}
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Area Associativa: arrivano le Comunicazioni tra rappresentanti
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">12 Giugno 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'•'}</span>
                      <span>Nel menu dell'Area Associativa la voce <strong>"Comunicazioni" è ora attiva</strong>: una chat in tempo reale per parlare con gli altri rappresentanti, senza passare da email o telefono.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'•'}</span>
                      <span><strong>Con chi puoi scrivere:</strong> i rappresentanti con la tua <strong>stessa carica</strong> in tutta Italia e il tuo <strong>riferimento territoriale di grado superiore</strong> (es. dal segretario provinciale al segretario regionale della propria regione). I contatti sono già organizzati per te in "Pari grado", "Riferimento territoriale" e "Il tuo territorio".</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'•'}</span>
                      <span><strong>Come una vera app di messaggi:</strong> vedi chi è online, chi sta scrivendo, i messaggi non letti (anche nel menu laterale) e ricevi tutto all'istante. Funziona bene anche da telefono.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'•'}</span>
                      <span><strong>Stanze di gruppo:</strong> la segreteria UNASCA può creare stanze con più rappresentanti (es. un gruppo di coordinamento) — le trovi nella stessa pagina, pronte all'uso.</span>
                    </li>
                  </ul>
                </div>
                {/* Dashboard area associativa rinnovata */}
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="ux">UX</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Area Associativa: la dashboard ti dice cosa fare oggi
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">12 Giugno 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Il <strong>benvenuto è personalizzato</strong>: il tuo territorio di competenza, la data di oggi e un riassunto di cosa ti aspetta (bozze da completare, prossimo appuntamento).</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span><strong>Numeri a colpo d'occhio</strong>: eventi in programma, eventi del mese, cariche attive e associati del tuo perimetro.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Nuovo riquadro <strong>"Da fare"</strong>: gli eventi rimasti in bozza sono lì, con un click li riprendi da dove avevi lasciato. Accanto, l'<strong>agenda</strong> con i prossimi eventi e i conclusi di recente.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span><strong>Invitare è più semplice</strong>: niente più "Genera inviti" — selezioni le persone e con un solo pulsante gli inviti partono. Se aggiungi qualcuno dopo l'invio, il sistema spedisce solo gli inviti mancanti.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Il <strong>menu laterale si può comprimere</strong> per avere più spazio, la modifica del profilo si apre in una finestra dedicata e l'area funziona bene anche <strong>da telefono</strong>.</span>
                    </li>
                  </ul>
                </div>
                {/* Regia guidata eventi */}
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Gestire un evento è molto più semplice
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">12 Giugno 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'•'}</span>
                      <span>Ora una <strong>sola pagina ti accompagna passo-passo</strong>: dati dell'evento, invitati, pubblicazione, presenze e verbale, in ordine. In ogni momento c'è <strong>un solo pulsante verde</strong> che ti dice cosa fare adesso.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'•'}</span>
                      <span><strong>Pubblicare e invitare in un click:</strong> il pulsante «Pubblica e invia inviti» fa tutto insieme, con una richiesta di conferma chiara. Niente più passaggi nascosti nei menù.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'•'}</span>
                      <span>Creare un evento è più rapido: bastano <strong>nome, tipo e data</strong>; il resto (orario, luogo, ordine del giorno) lo aggiungi solo se vuoi.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'•'}</span>
                      <span><strong>Presenze più veloci:</strong> «Segna tutti presenti» con un tocco e poi correggi le eccezioni. E quando scrivi il verbale, un promemoria ti ricorda di segnare prima i presenti.</span>
                    </li>
                  </ul>
                </div>

                {/* Eventi area associativa: settori, anteprima e visibilità più precisa */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Eventi dell'Area Associativa più precisi e trasparenti
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">11 Giugno 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Se ricopri una carica associativa, nel calendario ora vedi solo gli eventi che riguardano davvero <strong>i tuoi settori e il tuo territorio</strong>: niente più eventi di altre province o di settori che non ti competono.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Quando crei un evento puoi scegliere i <strong>settori</strong> a cui è rivolto (studi, autoscuole, nautica) e, per le cariche nazionali, anche le <strong>cariche destinatarie</strong> specifiche — ad esempio una riunione riservata ai soli Consiglieri Nazionali.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Nuova <strong>anteprima destinatari</strong>: prima di pubblicare vedi quante persone vedranno l'evento e con quali cariche, così non ci sono sorprese.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Messaggi più chiari quando una funzione non è disponibile per la tua carica (ad esempio l'invito degli associati), con l'indicazione di cosa serve per attivarla.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span><strong>Inviti in un click:</strong> niente più selezione uno a uno — puoi invitare <strong>tutti gli associati del tuo territorio</strong> o <strong>i titolari di una carica</strong> (es. convocare la Direzione Nazionale, che riceve l'invito sulla propria email personale), con un'<strong>anteprima</strong> che ti dice quanti destinatari riceveranno l'invito prima di confermare.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span><strong>Registro presenze:</strong> se organizzi un evento, la nuova scheda "Presenze" ti permette di segnare chi è presente con un tocco, con contatore e ricerca — perfetta da usare all'ingresso.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span><strong>Allegati all'invito:</strong> puoi aggiungere PDF e immagini (convocazioni, mappe, programmi) che gli invitati troveranno come link nell'email di invito. Anche l'email ha una nuova veste grafica più professionale.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span><strong>Vista calendario:</strong> nel Calendario Attività puoi passare dalla lista a una <strong>griglia mensile</strong> per vedere a colpo d'occhio gli eventi del mese, con navigazione tra i mesi.</span>
                    </li>
                  </ul>
                </div>
                {/* Controllo notifiche push e in-app */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Gestione più sicura delle notifiche
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">5 Giugno 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Abbiamo aggiunto due interruttori che permettono allo staff UNASCA di attivare o sospendere in qualsiasi momento le notifiche push del browser e le notifiche nella campanella, in modo indipendente. Un miglioramento dietro le quinte che rende il sistema di notifiche più affidabile e controllabile.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Le notifiche degli <strong>eventi</strong> (inviti, promemoria e check-in) ora compaiono nella campanella <strong>in tempo reale</strong>, senza bisogno di ricaricare la pagina.</span>
                    </li>
                  </ul>
                </div>
                {/* Ingresso più rapido agli eventi */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Ingresso più rapido agli eventi
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">1 Giugno 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'•'}</span>
                      <span>Quando nello stesso giorno ci sono più eventi che condividono la stessa fila d'ingresso, il tuo QR code viene riconosciuto subito anche se l'addetto sta gestendo un altro evento: niente più attese per cambiare evento allo scanner.</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* 2026-05 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2026-05')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Maggio 2026</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">7 aggiornamenti</p>
                </div>
              </div>
              {expandedSections.has('2026-05') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2026-05') && (
              <div className="px-6 pb-6 space-y-6">
                {/* Svincolo Atti: scelta sede */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex flex-wrap items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <Badge type="fix">Fix</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Svincolo Atti: scegli la sede dove svolgere l'atto
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">30 Maggio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'•'}</span>
                      <span>Ora puoi scegliere la sede esatta di uno studio (anche una sede secondaria) come luogo dove far svolgere l'atto.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'•'}</span>
                      <span>Lo studio riceve l'indicazione della sede richiesta via email e notifica.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'•'}</span>
                      <span>Non è più possibile selezionare lo stesso studio in due sedi diverse: ti avvisiamo se provi a farlo.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'•'}</span>
                      <span>Risolto un errore che impediva l'invio della richiesta quando si selezionava una sede secondaria sulla mappa.</span>
                    </li>
                  </ul>
                </div>
                {/* Avviso quando inizia una diretta */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Avviso quando inizia una diretta
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">28 Maggio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'•'}</span>
                      <span>Quando inizia una diretta di un evento a cui sei invitato ricevi una notifica (e, se previsto, un'email) con il link per seguirla in tempo reale.</span>
                    </li>
                  </ul>
                </div>
                {/* Diretta Live degli eventi */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Diretta Live degli eventi
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">27 Maggio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'•'}</span>
                      <span>Segui in tempo reale i Consigli Nazionali e gli eventi: foto, video e aggiornamenti pubblicati durante la giornata, tutto in una pagina che si aggiorna da sola. Alcune dirette saranno accessibili anche tramite link pubblico condivisibile.</span>
                    </li>
                  </ul>
                </div>
                {/* Rappresentanti: accesso rapido all'area dello studio */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Rappresentanti: accesso rapido all'area del tuo studio
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">22 Maggio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'•'}</span>
                      <span>Se sei un rappresentante associativo collegato a uno studio, dall'area associativa puoi ora entrare direttamente nell'area riservata del tuo studio con il pulsante in alto, senza rifare l'accesso.</span>
                    </li>
                  </ul>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="fix">Fix</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Quota integrativa: corretto l'avviso di pagamento per chi l'aveva già pagata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">20 Maggio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">{'•'}</span>
                      <span>Se nel tuo profilo è presente una quota integrativa (per esempio sei uno Studio con integrativa Autoscuola) e l'avevi già pagata, l'area riservata <strong>non mostra più per errore</strong> l'avviso "Quota integrativa da pagare".</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">{'•'}</span>
                      <span>L'avviso di pagamento compare ora <strong>solo</strong> quando la segreteria aggiunge effettivamente una nuova quota integrativa da saldare.</span>
                    </li>
                  </ul>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="fix">Fix</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Mappa Rete Certificata: ora mostra solo studi con certificato attivo
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">13 Maggio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">{'•'}</span>
                      <span>La mappa pubblica della Rete Certificata e la ricerca studi nello Svincolo Atti ora mostrano <strong>solo gli studi con certificato attivo</strong> (con quota pagata). Gli studi con pratica di rinnovo in attesa di pagamento non sono piu visibili pubblicamente.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">{'•'}</span>
                      <span>Questo garantisce che eventuali contatti per pratiche di svincolo arrivino solo a studi effettivamente in regola.</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* 2026-04 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2026-04')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Aprile 2026</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">8 aggiornamenti</p>
                </div>
              </div>
              {expandedSections.has('2026-04') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2026-04') && (
              <div className="px-6 pb-6 space-y-6">
                {/* Svincolo Atti — Chat e miglioramenti */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novita</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Messaggi nella pratica di svincolo
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">11 Aprile 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'\u2022'}</span>
                      <span>Ora puoi scambiare messaggi direttamente nella pratica di svincolo atto, senza bisogno di email o telefonate</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'\u2022'}</span>
                      <span>Puoi aggiungere note per gli studi gia al momento della creazione della richiesta</span>
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Caricamento documenti semplificato
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">11 Aprile 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 ml-3">
                    Non sei piu vincolato a caricare esattamente 3 documenti separati. Puoi caricare un unico PDF con tutte le pagine o quanti file vuoi, assegnando un nome a ciascuno.
                  </p>
                </div>

                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="fix">Fix</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Correzioni svincolo atti
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">11 Aprile 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">{'\u2022'}</span>
                      <span>Corretto un problema per cui il rifiuto di uno studio non veniva mostrato correttamente</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">{'\u2022'}</span>
                      <span>Le email di notifica ora mostrano correttamente il nome dello studio</span>
                    </li>
                  </ul>
                </div>

                {/* Verbale Evento con AI */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novita</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Verbale con Assistente AI
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">5 Aprile 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 ml-3 mb-3">
                    Nella scheda di dettaglio dei tuoi eventi trovi ora la tab <strong>Verbale</strong>.
                    Puoi redigere il verbale dell'assemblea con un editor completo e farti assistere
                    dall'<strong>intelligenza artificiale</strong>: l'assistente conosce i dettagli del tuo evento,
                    chi era presente e l'ordine del giorno, e ti aiuta a scrivere il verbale in modo professionale.
                    Puoi anche allegare documenti (PDF, immagini, ecc.).
                  </p>
                </div>

                {/* Calendario Attivita */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novita</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Calendario Attivita
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">5 Aprile 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 ml-3 mb-3">
                    Nuovo strumento <strong>Calendario</strong> nell'area associativa. Puoi creare eventi e assemblee per il tuo territorio,
                    invitare gli associati con email, notifica e push, e gestire i tuoi eventi in autonomia.
                    La visibilita nel calendario si adatta automaticamente alla tua carica: vedrai i tuoi eventi e quelli che ti riguardano.
                  </p>
                </div>

                {/* Strumento Associati */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novita</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Nuovo strumento: Associati
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">4 Aprile 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 ml-3 mb-3">
                    Dall'area associativa puoi ora consultare la <strong>lista degli associati</strong> del tuo territorio.
                    Troverai filtri di ricerca per stato, categoria, tesseramento e molto altro, insieme a una scheda dettaglio per ogni associato.
                    La lista si adatta automaticamente al tuo ruolo: se sei un rappresentante provinciale vedrai solo gli associati della tua provincia,
                    se sei un rappresentante regionale vedrai quelli della tua regione, e i ruoli nazionali vedono tutti.
                    Anche il settore viene rispettato: se la tua carica è "Studi", vedrai solo gli associati con categoria Studio (o con integrativa Studio).
                  </p>
                </div>

                {/* Area Associativa per Rappresentanti */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novita</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Area Associativa per Rappresentanti
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">5 Aprile 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 ml-3 mb-3">
                    Se ricopri una carica associativa all'interno dell'associazione (es. Segretario Provinciale, Componente Segreteria Nazionale, ecc.),
                    nella tua area riservata troverai una nuova card <strong>"Area Associativa"</strong> che ti da accesso a uno spazio dedicato al tuo ruolo.
                  </p>
                  <ul className="space-y-1.5 ml-6 text-sm text-gray-700 dark:text-gray-300">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'\u2022'}</span>
                      <span><strong>Profilo personale:</strong> i tuoi dati, la foto profilo, le cariche attive e l'impresa collegata.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'\u2022'}</span>
                      <span><strong>Email personale:</strong> puoi impostare un'email personale per accedere direttamente all'area associativa dal login, senza passare dall'area impresa.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'\u2022'}</span>
                      <span><strong>Protezione PIN:</strong> se vuoi impedire ai dipendenti della tua impresa di accedere all'area associativa, puoi impostare un codice PIN personale.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'\u2022'}</span>
                      <span><strong>Prossimamente:</strong> calendario attivita, comunicazioni riservate tra rappresentanti e archivio documenti per ruolo.</span>
                    </li>
                  </ul>
                </div>

                {/* Svincolo Atti — Prenotazione in giornata */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Svincolo Atti — Prenotazione in giornata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">1 Aprile 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 ml-3 mb-3">
                    Ora puoi prenotare un appuntamento per lo svincolo atti anche nella stessa giornata. In precedenza era necessario attendere almeno il giorno successivo.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* 2026-03 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2026-03')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Marzo 2026</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">13 aggiornamenti</p>
                </div>
              </div>
              {expandedSections.has('2026-03') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2026-03') && (
              <div className="px-6 pb-6 space-y-6">
                {/* Recensioni Servizi */}
                <div className="border-l-4 border-yellow-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Nuovo sistema di recensioni per i servizi della Rete Certificata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">31 Marzo 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 ml-3 mb-3">
                    Dopo aver completato un servizio (Svincolo Atti, Rimborso Voucher o Collabora), riceverai un invito a lasciare la tua opinione. Le migliori recensioni saranno visibili nella pagina della Rete Certificata.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-500 mt-1">{'\u2022'}</span>
                      <span>Valutazione con stelle (1-5) e tag rapidi per descrivere l'esperienza</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-500 mt-1">{'\u2022'}</span>
                      <span>Banner nella dashboard per le recensioni in attesa</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-500 mt-1">{'\u2022'}</span>
                      <span>Le recensioni approvate sono visibili nella pagina pubblica della Rete Certificata</span>
                    </li>
                  </ul>
                </div>

                {/* Collabora con altri membri */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novità</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Collabora con altri membri della Rete Certificata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">30 Marzo 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 ml-3 mb-3">
                    Nuova sezione dedicata alla collaborazione tra colleghi della Rete Certificata! Puoi chiedere supporto o collaborare con un altro membro per tutte le tipologie di pratiche (immatricolazioni, trasporti, nazionalizzazioni, consulenze tecniche, ecc.) — al di fuori dello Svincolo Atti e del Rimborso Voucher che restano gestibili dalle rispettive sezioni dedicate.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Procedura guidata:</strong> scegli il tipo di pratica, trova il collega sulla mappa (filtrabile per regione, provincia, città o nome studio), scrivi il tuo messaggio e invia la richiesta</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Messaggi in tempo reale:</strong> la conversazione con il collega si aggiorna istantaneamente, senza bisogno di ricaricare la pagina</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Notifiche complete:</strong> ricevi avvisi via email, notifica push e campanella quando un collega ti contatta o risponde</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Guida interattiva:</strong> al primo accesso una guida passo-passo ti spiega come utilizzare la sezione. Puoi riaprirla in qualsiasi momento cliccando il bottone ?</span>
                    </li>
                  </ul>
                </div>

                {/* Area Riservata — Miglioramenti card */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Area Riservata — Nuova organizzazione servizi
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">30 Marzo 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'\u2022'}</span>
                      <span>La sezione <strong>I miei eventi</strong> è ora accessibile a tutti gli associati, non solo ai membri della Rete Certificata</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'\u2022'}</span>
                      <span>Le card riservate alla Rete Certificata appaiono ora in grigio con lucchetto per chi non è ancora membro, con un messaggio informativo al click</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'\u2022'}</span>
                      <span>Nuova card <strong>"Non sei ancora nella Rete Certificata?"</strong> in fondo alla pagina per scoprire i vantaggi e iscriversi</span>
                    </li>
                  </ul>
                </div>

                {/* Feedback — Redesign */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Nuova sezione Contattaci &amp; Feedback
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">26 Marzo 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 ml-3">
                    La sezione feedback è stata completamente rinnovata con un nuovo design. Ora puoi vedere tutti i tuoi messaggi in un'unica pagina, filtrare per stato e tipo, e comunicare direttamente con il team UNASCA tramite una conversazione in stile chat.
                  </p>
                </div>

                {/* Voucher Rimborso — Importi completi */}
                <div className="border-l-4 border-orange-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="fix">Correzione</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Rimborso Voucher — Visualizzazione importi completa
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">25 Marzo 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 mt-1">{'\u2022'}</span>
                      <span>La ricerca pratiche nella sezione rimborso voucher ora mostra <strong>tutti gli importi</strong> della tariffa (Gas Compressi, Diritti STED, Contributi, Tributi Speciali, ecc.) — prima erano visibili solo Diritti e Bolli</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 mt-1">{'\u2022'}</span>
                      <span>Corretti i totali nelle pagine di dettaglio e lista voucher che non includevano tutte le voci</span>
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novita</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        I Miei Eventi
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">20 Marzo 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'\u2022'}</span>
                      <span>Nuova sezione <strong>"I miei eventi"</strong> nell'area riservata: consulta gli eventi UNASCA a cui sei stato invitato</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'\u2022'}</span>
                      <span><strong>QR code personale</strong> — Ogni invito include un QR code da mostrare all'ingresso per la registrazione automatica della presenza</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'\u2022'}</span>
                      <span><strong>Notifiche automatiche</strong> — Ricevi avvisi per nuovi inviti, promemoria 48h e 24h prima dell'evento e conferma presenza registrata</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'\u2022'}</span>
                      <span>Il QR code è sempre disponibile nella tua area riservata anche se hai già ricevuto l'email di invito</span>
                    </li>
                  </ul>
                </div>

                {/* Certificato Rete Certificata */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novita</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Certificato Rete Certificata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">4 Marzo 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 ml-3">
                    Ora è possibile visualizzare il numero del tuo certificato IPZS e scaricare la copia digitale direttamente dalla tua area riservata, nella sezione Rete Certificata. Sarete avvisati appena sarà disponibile il certificato.
                  </p>
                </div>

                <p className="text-sm text-gray-400 dark:text-gray-500 italic text-center py-4">Aggiornamenti in arrivo...</p>
                {/* TODO: cambiare false in true quando pronti per mostrare agli associati */}
                {false && (<>
                                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novita</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Maxischermo interattivo per eventi
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">22 Marzo 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 ml-3">
                    Nuovo maxischermo interattivo per eventi — visualizzazione animata dei check-in in tempo reale
                  </p>
                </div>

                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novita</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Community in tempo reale e nuove reazioni
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">2 Marzo 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Aggiornamenti in tempo reale</strong> — Commenti e reazioni appaiono istantaneamente senza ricaricare la pagina, sia nel lounge che nelle discussioni</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Reazioni stile social</strong> — Clicca "Mi piace" per reagire rapidamente, passa il mouse per scegliere tra tutte le emoji disponibili</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Risposte nidificate</strong> — I commenti ai commenti vengono raggruppati con linee di connessione, mostrando "In risposta a @Nome" per capire il contesto</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Ultimi commenti</strong> — Entrando in una discussione vedi subito gli ultimi 10 commenti, con possibilita di caricare quelli precedenti</span>
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Navigazione e interfaccia migliorata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">2 Marzo 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'\u2022'}</span>
                      <span><strong>Sidebar con icone</strong> — La barra laterale nelle aree mostra le icone di ogni sezione, anche quando minimizzata</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'\u2022'}</span>
                      <span><strong>Ricerca in modalita scura</strong> — Il campo di ricerca ora e visibile correttamente anche con il tema scuro</span>
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novita</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Risposte UNASCA Staff e tooltip reazioni
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">2 Marzo 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Risposte Staff</strong> — L'amministrazione UNASCA ora può rispondere nelle discussioni come "UNASCA Staff", con badge e colore distinto</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Chi ha messo like?</strong> — Posizionando il mouse sulle reazioni ora puoi vedere chi le ha messe</span>
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Miglioramenti alle risposte accettate
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">2 Marzo 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'\u2022'}</span>
                      <span><strong>Accettazioni multiple</strong> — Ora puoi accettare più risposte nella stessa discussione</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'\u2022'}</span>
                      <span><strong>Badge migliorato</strong> — Il badge "Risposta accettata" è più visibile, posizionato sotto il commento</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'\u2022'}</span>
                      <span><strong>Vista completa</strong> — Tutte le discussioni (aperte e risolte) sono mostrate insieme di default</span>
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-violet-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="ux">UX</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Community — Nuovo design e foto profilo
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">1 Marzo 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-violet-500 mt-1">{'\u2022'}</span>
                      <span><strong>Aspetto rinnovato</strong> — Header ridisegnato, card con bordi eleganti e animazioni su hover, sidebar migliorata</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-violet-500 mt-1">{'\u2022'}</span>
                      <span><strong>Foto profilo</strong> — La tua foto caricata nell'Area Riservata viene mostrata come avatar in tutta la Community</span>
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novita</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Modifica ed elimina i tuoi commenti
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">1 Marzo 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span>Passa il mouse su un tuo commento per vedere le icone Modifica e Elimina. I commenti modificati mostrano il badge "(modificato)"</span>
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Novita</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Community Rete Certificata v2
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">1 Marzo 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span>Nuova versione della Community completamente riprogettata! Aree tematiche, discussioni con risposte accettate, reazioni, messaggi privati, ricerca e sistema di reputazione con badge</span>
                    </li>
                  </ul>
                </div>
                </>)}
              </div>
            )}
          </div>

          {/* 2026-02 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2026-02')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Febbraio 2026</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">14 aggiornamenti</p>
                </div>
              </div>
              {expandedSections.has('2026-02') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2026-02') && (
              <div className="px-6 pb-6 space-y-6">
                {/* Sondaggi — Miglioramenti compilazione */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Sondaggi — Compilazione migliorata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">28 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Domande a matrice</strong> — Ora puoi rispondere alle domande con tabella a righe e colonne (scelta singola o multipla)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Firma digitale</strong> — Disegna la tua firma direttamente sullo schermo con mouse o touch</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Salvataggio più fluido</strong> — Le risposte testuali vengono salvate dopo che hai finito di scrivere, senza rallentamenti</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Errori risolti</strong> — Corretti problemi che impedivano il salvataggio di alcune risposte</span>
                    </li>
                  </ul>
                </div>

                {/* Rete Certificata — Nuovo design pubblico */}
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Rete Certificata — Pagina rinnovata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">28 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'\u2022'}</span>
                      <span><strong>Ricerca studi per attività</strong> — Filtra gli studi certificati per tipo di attività (pratiche auto, nautica, ecc.)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'\u2022'}</span>
                      <span><strong>Design rinnovato</strong> — Pagina pubblica completamente ridisegnata, più chiara e facile da navigare</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">{'\u2022'}</span>
                      <span><strong>Sezione dedicata studi</strong> — Area separata per la gestione della certificazione del tuo studio</span>
                    </li>
                  </ul>
                </div>

                {/* Redesign Area Riservata */}
                <div className="border-l-4 border-violet-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Redesign Dashboard Area Riservata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">27 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-violet-500 mt-1">•</span>
                      <span><strong>Contatti inline</strong> — Email secondarie, telefoni extra e PEC ora visibili e modificabili direttamente nella scheda contatti, senza modal</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-violet-500 mt-1">•</span>
                      <span><strong>Salvataggio immediato</strong> — Aggiungere o rimuovere un contatto salva immediatamente, senza bottone "Salva"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-violet-500 mt-1">•</span>
                      <span><strong>Attività svolte</strong> — Nuova sezione per selezionare le attività del tuo studio (categorie STUDIO / AUTOSCUOLA / NAUTICA)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-violet-500 mt-1">•</span>
                      <span><strong>Copia codice</strong> — Clicca sul tuo codice associato per copiarlo negli appunti</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-violet-500 mt-1">•</span>
                      <span><strong>Design rinnovato</strong> — Animazioni staggered, shimmer sulla hero card, saluto contestuale e ombre colorate per tipo carta</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-violet-500 mt-1">•</span>
                      <span><strong>Mobile ottimizzato</strong> — Header sempre visibile con nome troncato, bottoni compatti su smartphone</span>
                    </li>
                  </ul>
                  <img
                    src="/images/changelog/area_riservata.jpg"
                    alt="Redesign Dashboard Area Riservata"
                    className="mt-4 rounded-lg shadow-md w-full h-auto border border-violet-200 dark:border-violet-700"
                  />
                </div>

                {/* Centro Notifiche Push */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Notifiche Push Browser
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">24 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Notifiche browser native</strong> - Ricevi avvisi direttamente sul tuo dispositivo anche quando il sito non è aperto</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Attiva dalla campanella</strong> - Usa lo slider in basso nel pannello notifiche per abilitare/disabilitare</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">{'\u2022'}</span>
                      <span><strong>Filtri per categoria</strong> - Icone compatte per filtrare notifiche per tipo (sondaggi, comunicazioni, ecc.)</span>
                    </li>
                  </ul>
                </div>

                {/* Video Tutorial Doppi */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Video Tutorial - Due guide disponibili
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">22 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'\u2022'}</span>
                      <span><strong>Come funziona il rimborso</strong> - Video tutorial che spiega il servizio di rimborso voucher PagoPA</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'\u2022'}</span>
                      <span><strong>Come creare un Voucher</strong> - Nuovo video che mostra come creare un voucher dal portale del trasporto</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">{'\u2022'}</span>
                      <span><strong>Selezione video</strong> - Scegli quale video guardare direttamente dal popup</span>
                    </li>
                  </ul>
                </div>

                {/* Elimina Voucher Propri */}
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Elimina voucher propri se disponibili
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">21 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">{'\u2022'}</span>
                      <span><strong>Pulsante elimina</strong> - Nel tab "I Miei", i voucher disponibili hanno un pulsante elimina</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">{'\u2022'}</span>
                      <span><strong>Elimina se non usati</strong> - Puoi eliminare i voucher che hai creato ma non hai ancora utilizzato</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">{'\u2022'}</span>
                      <span><strong>Conferma eliminazione</strong> - Un messaggio di conferma ti chiede di verificare prima di eliminare</span>
                    </li>
                  </ul>
                </div>

                {/* Dettagli Pagamento nel Popup Prenotazione */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Dettagli pagamento nel popup prenotazione
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">21 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'\u2022'}</span>
                      <span><strong>Importi visibili</strong> - Quando prenoti un voucher, il popup mostra il totale da pagare</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'\u2022'}</span>
                      <span><strong>Breakdown tariffe</strong> - Vedi il dettaglio di diritti, bolli, contributi e altri importi</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">{'\u2022'}</span>
                      <span><strong>Calcolo quantità</strong> - Se prenoti più voucher, il totale viene moltiplicato automaticamente</span>
                    </li>
                  </ul>
                </div>

                {/* Voucher Cumulativi */}
                <div className="border-l-4 border-amber-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Possibilità di creare anche Voucher con iuv cumulativi
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">20 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1">{'\u2022'}</span>
                      <span><strong>Pratiche cumulative</strong> - Possibilit{'a\u0300'} di creare un unico voucher per pi{'u\u0300'} pratiche con lo stesso codice.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1">{'\u2022'}</span>
                      <span><strong>Anteprima calcolo</strong> - Visualizza in tempo reale il totale cumulativo e la differenza rispetto a pratiche singole</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1">{'\u2022'}</span>
                      <span><strong>Badge cumulativo</strong> - I voucher cumulativi sono evidenziati con un badge arancione nelle liste</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <p className="text-xs text-amber-800 dark:text-amber-300 font-medium">
                      {'\u26A1'} Nota: Le pratiche cumulative Motorizzazione permettono di pagare bolli ridotti quando si presentano pi{'u\u0300'} pratiche identiche insieme
                    </p>
                  </div>
                </div>

                {/* Centro Notifiche Unificato */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Centro notifiche unificato
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">19 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span><strong>Campanella globale nel header</strong> - Notifiche real-time da tutti i servizi: voucher, atti svincolo, feedback, sondaggi e changelog</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span><strong>Raggruppamento automatico</strong> - Messaggi multipli della stessa persona raggruppati con contatore</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span><strong>Filtri per categoria</strong> - Visualizza notifiche per tipo (Voucher, Atti, Feedback, Sondaggi, Sistema)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span><strong>Deep linking</strong> - Clicca notifica per andare direttamente al dettaglio</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-xs text-green-800 dark:text-green-300 font-medium">
                      🔔 Tutte le notifiche in un unico punto, aggiornate in tempo reale senza refresh
                    </p>
                  </div>
                </div>

                {/* Chat Voucher - Notifiche Real-time */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Chat voucher - notifiche real-time
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">16 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span><strong>Notifiche istantanee</strong> - Ogni messaggio nella chat voucher genera notifica real-time</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span><strong>Skip se in chat</strong> - Nessuna notifica se sei già nella stanza chat</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span><strong>Auto-mark read</strong> - Notifiche marcate come lette automaticamente all'ingresso in chat</span>
                    </li>
                  </ul>
                </div>

                {/* Fix Notifiche Real-time Sincronizzazione */}
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="fix">Fix</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Notifiche real-time - sincronizzazione
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">15 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span><strong>Corretto aggiornamento real-time</strong> - Le notifiche raggruppate si aggiornano istantaneamente senza necessità di refresh</span>
                    </li>
                  </ul>
                </div>

                {/* UX Pagina Principale Rimborso Voucher */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="ux">UX</Badge>
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Rimborso Voucher - Pagina principale migliorata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">14 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">•</span>
                      <span><strong>Colori distintivi per tariffario</strong> - Ogni tariffario ha un colore unico per distinguerli meglio (Nazionale blu, Bolzano rosso, Trento verde, Sicilia ambra, Valle d'Aosta viola, Friuli V.G. teal)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">•</span>
                      <span><strong>5 card statistiche redesignate</strong> - Disponibili, I Miei, Prenotati da Me, Prenotazioni Ricevute, Messaggi con design moderno e hover effects</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">•</span>
                      <span><strong>Nuovo tab "Prenotazioni Ricevute"</strong> - Visualizza i tuoi voucher prenotati da altri associati con nome dello studio che ha prenotato</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">•</span>
                      <span><strong>Modal conferma prenotazione</strong> - Prima di prenotare, vedi un riepilogo con tariffario, codice pratica, descrizione, proprietario e quantità</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <p className="text-xs text-purple-800 dark:text-purple-300 font-medium">
                      💡 Benefici: Interfaccia più intuitiva e colorata, prenotazioni più consapevoli, visualizzazione completa dei voucher ricevuti
                    </p>
                  </div>
                </div>

                {/* Redesign UX Pagina Dettaglio Rimborso Voucher */}
                <div className="border-l-4 border-pink-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="ux">UX/UI</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Interfaccia più semplice per i rimborsi voucher
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">12 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-pink-500 mt-1">•</span>
                      <span><strong>Stato visibile</strong>: Un indicatore visivo mostra chiaramente a che punto sei nel processo (Prenotato → Attesa Conferme → Completato)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-pink-500 mt-1">•</span>
                      <span><strong>Azione principale evidente</strong>: Sai subito cosa devi fare, con un bottone blu ben visibile</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-pink-500 mt-1">•</span>
                      <span><strong>Confronto semplice</strong>: Vedi chiaramente i tuoi dati e quelli della controparte, uno accanto all'altro</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-pink-500 mt-1">•</span>
                      <span><strong>Meno informazioni inutili</strong>: I dettagli secondari sono nascosti, li vedi solo se ti servono</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-pink-500 mt-1">•</span>
                      <span><strong>Documenti organizzati</strong>: Voucher e ricevuta di pagamento in un'unica sezione, facili da trovare</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                    <p className="text-xs text-pink-800 dark:text-pink-300 font-medium">
                      ✨ Benefici: Meno confusione, meno errori, meno telefonate al supporto. Perfetto anche per chi non è molto pratico di tecnologia.
                    </p>
                  </div>
                </div>

                {/* Miglioramenti UX Pagina Dettaglio Rimborso Voucher */}
                <div className="border-l-4 border-cyan-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="ux">UX</Badge>
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Rimborso Voucher - Pagina dettaglio migliorata
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">7 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500 mt-1">•</span>
                      <span><strong>Stepper con step documenti</strong> - Vedi chiaramente a che punto sei: Prenotato → PDF Voucher → PDF Ricevuta → Conferme → Completato</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500 mt-1">•</span>
                      <span><strong>Bottone "Conferma Scambio" in alto</strong> - Più visibile e facile da trovare, con "Annulla prenotazione" sotto</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500 mt-1">•</span>
                      <span><strong>Messaggi personalizzati</strong> - Quando confermi, vedi un messaggio specifico per il tuo ruolo (proprietario o prenotante)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500 mt-1">•</span>
                      <span><strong>Aggiornamenti in tempo reale</strong> - Se l'altra parte annulla il voucher, lo vedi subito senza ricaricare la pagina</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500 mt-1">•</span>
                      <span><strong>Font più leggibili</strong> - Dettagli delle parti coinvolte (nome, codice, email, telefono) con caratteri ridotti per una visualizzazione più ordinata</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                    <p className="text-xs text-cyan-800 dark:text-cyan-300 font-medium">
                      ✨ Benefici: Processo più chiaro, meno confusione, aggiornamenti istantanei, meno necessità di ricaricare la pagina
                    </p>
                  </div>
                </div>

                {/* Web Push Notifications */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Web Push Notifications - Notifiche Browser Native
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">4 Febbraio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span><strong>Toggle attivazione</strong> - Abilita notifiche browser native direttamente dalla campanella nel header</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span><strong>Notifiche anche con tab chiusa</strong> - Ricevi notifiche anche quando non sei sul sito, come WhatsApp Web o Facebook</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span><strong>Multi-device</strong> - Attiva su PC, portatile, telefono - ogni dispositivo riceve le notifiche indipendentemente</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span><strong>Opt-in GDPR-friendly</strong> - Tu scegli se attivare, nessun tracciamento forzato</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                    <p className="text-xs text-indigo-800 dark:text-indigo-300 font-medium">
                      📱 Nota: Le notifiche browser funzionano su Chrome, Firefox, Edge, Safari 16.4+ (desktop e mobile)
                    </p>
                  </div>
                </div>

              </div>
            )}
          </div>

          {/* 2026-01 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2026-01')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-base font-bold text-gray-900 dark:text-white">Gennaio 2026</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">7 aggiornamenti</p>
                </div>
              </div>
              {expandedSections.has('2026-01') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2026-01') && (
              <div className="px-6 pb-6 space-y-6">
                {/* Zoom automatico mappa */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="improvement">Miglioramento</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Zoom automatico sulla mappa
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">19 Gennaio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span>Quando cerchi una <strong>provincia</strong> nella mappa della Rete Certificata, la mappa ora <strong>zooma automaticamente</strong> sulla zona selezionata</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span>Visualizzazione immediata di tutti gli studi nella provincia scelta</span>
                    </li>
                  </ul>
                </div>

                {/* Player Audio AI per Articoli */}
                <div className="border-l-4 border-violet-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Ascolta gli articoli con l'intelligenza artificiale
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">17 Gennaio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-violet-500 mt-1">•</span>
                      <span><strong>Conversione automatica</strong>: Gli articoli del blog vengono convertiti in audio tramite intelligenza artificiale</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-violet-500 mt-1">•</span>
                      <span><strong>Player integrato</strong>: Ascolta l'articolo direttamente dalla pagina, senza scaricare nulla</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-violet-500 mt-1">•</span>
                      <span><strong>Voce naturale</strong>: Lettura fluida e comprensibile grazie alla tecnologia AI</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg">
                    <p className="text-xs text-violet-800 dark:text-violet-300 font-medium">
                      💡 Benefici: Leggi gli articoli anche quando sei in movimento o hai le mani occupate, ideale per rimanere aggiornati durante gli spostamenti
                    </p>
                  </div>
                </div>

                {/* Mappa Rete Certificata - Sedi Secondarie */}
                <div className="border-l-4 border-teal-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Sedi secondarie visibili sulla mappa
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">14 Gennaio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-teal-500 mt-1">•</span>
                      <span>La <strong>mappa della Rete Certificata</strong> ora mostra tutte le sedi degli associati</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-500 mt-1">•</span>
                      <span>Marker <strong>verdi</strong> per le sedi principali, <strong>blu</strong> per le sedi secondarie</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-500 mt-1">•</span>
                      <span>Popup informativi con indirizzo e contatti per ogni sede</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                    <p className="text-xs text-teal-800 dark:text-teal-300 font-medium">
                      💡 Benefici: Maggiore visibilità per tutte le tue sedi sul territorio, utile per colleghi e clienti
                    </p>
                  </div>
                </div>

                {/* Codici STA e Matricole */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Sistema codici STA e matricole
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">04 Gennaio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span>Visualizzazione <strong>Codice STA</strong> personale nell'area riservata</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span>Visualizzazione <strong>Matricola</strong> personale nell'area riservata</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span>Card dedicata con design moderno e possibilità di copiare i codici</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-xs text-green-800 dark:text-green-300 font-medium">
                      💡 Benefici: Accesso rapido ai tuoi codici identificativi, necessari per la rete certificata e possibilità di condividerli facilmente durante lo scambio dei voucher
                    </p>
                  </div>
                  <img
                    src="/images/changelog/codice_sta.jpg"
                    alt="Sistema Codici STA e Matricole"
                    className="mt-4 rounded-lg shadow-md w-full h-auto border border-green-200 dark:border-green-700"
                  />
                </div>

                {/* Pagamento Bonifico Bancario */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Pagamento quota Rete Certificata con bonifico bancario
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">06 Gennaio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span><strong>Alternativa al pagamento con carta</strong>: Ora puoi pagare anche tramite bonifico bancario</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span><strong>Scelta tipo bonifico</strong>: Bonifico istantaneo o normale (2-3 giorni)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span><strong>Upload distinta</strong>: Carica la ricevuta del bonifico effettuato (PDF, JPG, PNG)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span><strong>Conferma automatica</strong>: Ricevi email con ricevuta dopo la verifica della Segreteria Nazionale</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-xs text-blue-800 dark:text-blue-300 font-medium">
                      💡 Benefici: Maggiore flessibilità nei metodi di pagamento, ideale per chi preferisce il bonifico bancario alla carta di credito
                    </p>
                  </div>
                  <img
                    src="/images/changelog/bonifico.jpg"
                    alt="Pagamento con Bonifico Bancario"
                    className="mt-4 rounded-lg shadow-md w-full h-auto border border-blue-200 dark:border-blue-700"
                  />
                </div>

                {/* Sistema Sondaggi */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Sistema sondaggi multipagina
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Gennaio 2026</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span><strong>Sondaggi multistep</strong>: Compilazione guidata in più pagine</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span><strong>Tipi di domande multipli</strong>: Radio, checkbox, testo, scala, dropdown, file upload e altro</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span><strong>Logica condizionale</strong>: Risposte intelligenti che determinano il percorso del sondaggio</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span><strong>Barra di progresso</strong>: Traccia il tuo avanzamento nel sondaggio</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span><strong>Salvataggio automatico</strong>: I tuoi dati vengono salvati mentre compili</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span><strong>Risposta anonima</strong>: Opzione per compilare sondaggi in forma anonima</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-xs text-green-800 dark:text-green-300 font-medium">
                      💡 Benefici: Partecipa facilmente ai sondaggi UNASCA con un'esperienza guidata e intuitiva, le tue risposte aiutano a migliorare la piattaforma e i servizi per tutti gli associati
                    </p>
                  </div>
                  <img
                    src="/images/changelog/sondaggi.jpg"
                    alt="Sistema Sondaggi Multipagina"
                    className="mt-4 rounded-lg shadow-md w-full h-auto border border-green-200 dark:border-green-700"
                  />
                </div>
              </div>
            )}
          </div>

          {/* 2025-12 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2025-12')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-base font-bold text-gray-900 dark:text-white">Dicembre 2025</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">1 aggiornamento</p>
                </div>
              </div>
              {expandedSections.has('2025-12') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2025-12') && (
              <div className="px-6 pb-6 space-y-6">
                <div className="border-l-4 border-amber-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="wip">In lavorazione</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Community UNASCA e chat
                      </h3>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span><strong>Community UNASCA</strong>: Area social dedicata agli associati</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span><strong>Chat in tempo reale</strong>: Messaggi istantanei tra associati</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span><strong>Feed di notizie</strong> e aggiornamenti della community</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span><strong>Notifiche</strong> per nuovi messaggi e interazioni</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <p className="text-xs text-amber-800 dark:text-amber-300 font-medium">
                      🚧 In sviluppo: Funzionalità community in fase di finalizzazione. Prossimamente disponibile per tutti gli associati!
                    </p>
                  </div>
                  <img
                    src="/images/changelog/community.jpg"
                    alt="Community UNASCA e Chat"
                    className="mt-4 rounded-lg shadow-md w-full h-auto border border-amber-200 dark:border-amber-700"
                  />
                </div>
              </div>
            )}
          </div>

          {/* 2025-11 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2025-11')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-base font-bold text-gray-900 dark:text-white">Novembre 2025</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">1 aggiornamento</p>
                </div>
              </div>
              {expandedSections.has('2025-11') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2025-11') && (
              <div className="px-6 pb-6 space-y-6">
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Blog pubblico UNASCA
                      </h3>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Blog con articoli e news UNASCA</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Design responsive ottimizzato per mobile</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Supporto dark mode completo</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Categorie e filtri per navigazione facile</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* 2025-10 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => toggleSection('2025-10')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-base font-bold text-gray-900 dark:text-white">Ottobre 2025</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">4 aggiornamenti importanti</p>
                </div>
              </div>
              {expandedSections.has('2025-10') ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.has('2025-10') && (
              <div className="px-6 pb-6 space-y-6">
                {/* Mappa RC */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Mappa Interattiva Rete Certificata
                      </h3>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span>Mappa nazionale degli studi certificati</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span>Ricerca per provincia e filtri geografici</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span>Dettagli e contatti degli studi</span>
                    </li>
                  </ul>
                </div>

                {/* Dark Mode */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="ux">UX</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Tema Scuro Completo
                      </h3>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 ml-3">
                    Supporto dark mode per tutte le pagine Rete Certificata con switch automatico
                  </p>
                </div>

                {/* Rete Certificata */}
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Sistema Rete Certificata Completo
                      </h3>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span><strong>Iscrizione online</strong> completamente digitale</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span>Form guidato con 14 sezioni</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span>Upload documenti formazione</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span><strong>Pagamenti online</strong> sicuri con carta</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span>Dashboard personale con stato domanda</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span>Certificati scaricabili in PDF</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                    <p className="text-xs text-emerald-800 dark:text-emerald-300 font-medium">
                      💡 Benefici: Processo completamente digitale, monitoraggio in tempo reale, pagamenti immediati
                    </p>
                  </div>
                </div>

                {/* Iscrizioni Soci */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Badge type="new">Nuovo</Badge>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        Sistema Iscrizioni Online
                      </h3>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-3">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Modulo iscrizione digitale completo</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Upload documenti (identità, visura, autorizzazioni)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Consensi privacy GDPR conformi</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Email conferma automatica</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Altre funzionalità storiche */}
          <div className="bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              Altre funzionalità disponibili
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Timeline UNASCA</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Storia interattiva dal 1977</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Convenzioni</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Sconti e vantaggi partner</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Rimborsi voucher</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Gestione digitale rimborsi</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Svincolo atti</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Richieste online e tracking</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Login OTP</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Accesso sicuro senza password</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-rose-500/20 flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Design moderno</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Responsive e dark mode</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Ultimo aggiornamento: <strong>24 Ottobre 2026</strong> (Eventi: il verbale si apre in una pagina leggibile con il PDF ufficiale da scaricare · Verbali: aggiungi le foto dell'evento e proponi un post social alla Segreteria Nazionale · Verbali: registra la riunione o carica un file audio, l'AI trascrive e scrive il verbale · Verbali: nuova pagina a tutta larghezza, assistente che impagina il testo, versioni e PDF ufficiale · I dipendenti dello studio anche nell'app · Chat per i dipendenti dello studio · In arrivo: accessi personali per i dipendenti dello studio · Verifica airbag Takata: Nissan, Mitsubishi, Subaru e camion Daimler di nuovo verificabili con l'estensione 1.2.5, esiti più precisi · Area Associativa: supporto più completo per i rappresentanti, foto profilo e meteo · Accesso con password accanto al codice via email · Immagini visibili nelle email e conferma dei voucher più chiara · Supporto: un unico posto per le tue richieste, anche via email · Area riservata ed estensione Takata: niente più «collegato» a vuoto · Verifica airbag Takata: Skoda automatica, camion DAF e Mercedes, FAQ della Segreteria Nazionale · Verifica airbag Takata: si parte dal registro, pannello rinnovato e nuovi esiti · Verifica airbag Takata: certificato con il logo del tuo studio, numero di pratica STA sul documento e davanti al nome del file scaricato, dichiarazione anche per acquirente società, dati completabili dal registro, portali Mercedes-Benz/Lexus/Seat/Peugeot/Citroën/DS/Suzuki sistemati e nuovi numeri di telaio riconosciuti · Verifica airbag Takata: più portali riconosciuti dall'estensione (BMW, Citroën, Peugeot, DS, Lexus, Ferrari, Jaguar, Land Rover, Seat, Mazda, Suzuki, Tesla), istruzioni su misura per ogni sito e avviso quando il telaio non viene compilato, nuova pagina «Stato dei portali» con segnalazione malfunzionamenti · Verifica airbag Takata: corretto un caso in cui l'esito poteva essere registrato sbagliato — ora conta solo il riquadro del risultato e in caso di dubbio la verifica non viene registrata · Tesseramento: notifiche in campanella per quote e pagamenti, badge delle quote integrative pagate, richieste integrative con risposta rapida della segreteria · Tesseramento: pagamenti più affidabili, stato rate RID visibile e quota per le nuove sedi pagabile dall'area riservata con ricevuta dedicata · Svincolo Atti: puoi aggiungere documenti dimenticati anche dopo l'invio · Area riservata riorganizzata in due schede (Servizi e Azioni · Il mio profilo) · Accesso diretto a Pratiche Auto Online dalla card Partnership Facile.it · Eventi: dettaglio più chiaro ed elegante · Eventi: scegli con quale carica pubblichi · Comunicazioni tra rappresentanti nell'Area Associativa · La dashboard dell'Area Associativa ti dice cosa fare oggi · Gestire un evento è molto più semplice · Eventi dell'Area Associativa più precisi e trasparenti · Gestione più sicura delle notifiche · Ingresso più rapido agli eventi · Dirette Live degli eventi · Accesso area riservata per rappresentanti interni)
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            Per supporto tecnico contattare la segreteria UNASCA
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default ChangelogPage;
